import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import QrReader from 'react-qr-reader'
import { Grid, Stack, Typography, Button } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { useGetFinishedGoodsQuery, useGetFinishedGoodByTagLazyQuery } from 'services/finishedGoods'
import { COMMON_STATUSES, PAGE_SIZE, PRICE_SHEET_TYPES } from 'utils/constants'
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
import DialogModal from '.'

const FinishedGoodsModal = ({ onClose, apiRef: materialsApiRef, priceSheetsData, onUpdateMaterial, ...restProps }) => {
  const apiRef = useGridApiRef()
  const qrReaderRef = useRef(null)
  const defaultQrCodeMessage = 'Please scan or upload your QR code'

  const { enablePullApartFeatures } = useSelector(state => state.featureFlags?.variables || {})
  const { defaultYardUm } = useSelector(state => state.settings)

  const [selectedFinishedGoodIds, setSelectedFinishedGoodIds] = useState([])
  const [cachedFinishedGoodData, setCachedFinishedGoodData] = useState({})
  const [result, setResult] = useState(defaultQrCodeMessage)
  const [scannedTag, setScannedTag] = useState(null)
  const [isQrInitialized, setIsQrInitialized] = useState(false)
  const [isQrLegacyMode, setIsQrLegacyMode] = useState(true)
  const [queryOptions, setQueryOptions] = useState({
    filter: {
      outboundTicketId: null,
      search: '',
      statuses: [COMMON_STATUSES.AVAILABLE.id],
      priceSheetId:
        priceSheetsData?.Items?.[0]?.type === PRICE_SHEET_TYPES.DEFAULT ? '' : priceSheetsData?.Items?.[0]?.id || '',
    },
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const { data: finishedGoodsData, isFetching: isFetchingFinishedGoods } = useGetFinishedGoodsQuery({
    ...queryOptions,
    filter: JSON.stringify(queryOptions.filter),
  })

  const [
    getFinishedGoodByTag,
    { data: scannedFinishedGoodData, error: scannedFinishedGoodError, isFetching: isFetchingScannedFinishedGood },
  ] = useGetFinishedGoodByTagLazyQuery({})

  const formatFinishedGood = finishedGood => ({
    id: finishedGood.id,
    fgId: finishedGood.tag,
    date: new Date(finishedGood.status?.date).toDateString(),
    type: finishedGood.type,
    itemCount: finishedGood.itemCount,
    materialCode: finishedGood.materialType?.code,
    materialName: finishedGood.materialType?.commonName,
    materialTypeId: finishedGood.materialType?.id,
    gross: finishedGood.weight?.gross,
    tare: finishedGood.weight?.tare,
    net: finishedGood.weight?.net,
    units: finishedGood.weight?.units,
  })

  const formattedFinishedGoods = useMemo(
    () => finishedGoodsData?.Items?.map(formatFinishedGood) || [],
    [finishedGoodsData],
  )

  const filteredFinishedGoods = useMemo(() => {
    if (formattedFinishedGoods && materialsApiRef && materialsApiRef.current?.getRowModels) {
      const tagList = []
      materialsApiRef.current?.getRowModels().forEach(row => {
        if (row.fgId !== undefined) {
          tagList.push(row.fgId)
        }
      })
      const fgList = []
      formattedFinishedGoods.forEach(fg => {
        if (!tagList.includes(fg.fgId)) {
          fgList.push(fg)
        }
      })
      return fgList
    }

    return []
  }, [formattedFinishedGoods, materialsApiRef])

  const { totalGross, totalTare, totalNet } = useMemo(
    () =>
      selectedFinishedGoodIds.reduce(
        (aggregate, finishedGoodId) => {
          const { gross, tare, net } = cachedFinishedGoodData[finishedGoodId]
          return {
            totalGross: aggregate.totalGross + Number(gross || 0),
            totalTare: aggregate.totalTare + Number(tare || 0),
            totalNet: aggregate.totalNet + Number(net || 0),
          }
        },
        { totalGross: 0, totalTare: 0, totalNet: 0 },
      ),
    [cachedFinishedGoodData],
  )

  const addNewMaterialWithFG = fg => {
    const finishedGood = cachedFinishedGoodData[fg]
    const firstPriceSheetEntry = priceSheetsData?.Items?.[0]?.entriesObj?.find(
      entry => entry.materialTypeId === finishedGood.materialTypeId,
    )

    materialsApiRef.current.updateRows([
      {
        id: finishedGood.id,
        orderEntryId: firstPriceSheetEntry?.id,
        isNew: true,
        code: {
          materialName: finishedGood.materialName,
          materialCode: finishedGood.materialCode,
          materialTypeId: finishedGood.materialTypeId,
          orderEntryId: firstPriceSheetEntry?.id,
        },
        fgId: finishedGood.fgId,
        fgType: finishedGood.type,
        itemCount: finishedGood.itemCount,
        yardGross: finishedGood.gross,
        yardTare: finishedGood.tare,
        yardNet: finishedGood.net,
      },
    ])
    materialsApiRef.current.scrollToIndexes({
      rowIndex: materialsApiRef.current.getRowsCount() - 1,
    })
  }

  const onAddFinishedGoods = useCallback(
    fgs => {
      onClose()

      fgs.forEach(fg => addNewMaterialWithFG(fg))
      onUpdateMaterial()
    },
    [cachedFinishedGoodData],
  )

  const handleScan = tag => {
    if (tag) {
      setScannedTag(tag)
      getFinishedGoodByTag({ tag })
    }
  }

  useEffect(() => {
    if (scannedFinishedGoodData && scannedFinishedGoodData?.tag === scannedTag) {
      const isAlreadyAdded = selectedFinishedGoodIds.includes(scannedFinishedGoodData.id)

      if (!isAlreadyAdded) {
        setSelectedFinishedGoodIds(prev => [...prev, scannedFinishedGoodData.id])
        setCachedFinishedGoodData(prevState => ({
          ...prevState,
          [scannedFinishedGoodData.id]: formatFinishedGood(scannedFinishedGoodData),
        }))
        setResult(`Added finished good with tag: ${scannedTag}.`)
      } else {
        setSelectedFinishedGoodIds(prev => prev.filter(id => id !== scannedFinishedGoodData.id))
        setResult(`Removed finished good with tag: ${scannedTag}.`)
      }
      setScannedTag(null)
    } else if (scannedFinishedGoodError) {
      setResult(`Error: finished good with tag ${scannedTag} not found.`)
      setScannedTag(null)
    }
  }, [scannedFinishedGoodData, scannedFinishedGoodError, scannedTag])

  const handleError = () => setResult('There was an error with the QR code.')

  const resetQRCodeReader = () => {
    setResult(defaultQrCodeMessage)
    setIsQrInitialized(false)
  }

  const fgColumns = useMemo(() => {
    const columns = [
      {
        field: 'fgId',
        headerName: 'FG #',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'type',
        headerName: 'Type',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'materialName',
        headerName: 'Material',
        width: 200,
        editable: false,
        sortable: false,
      },
      {
        field: 'itemCount',
        headerName: 'Count',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'gross',
        headerName: 'Gross',
        flex: 1,
        type: 'number',
        editable: false,
        sortable: false,
      },
      {
        field: 'tare',
        headerName: 'Tare',
        flex: 1,
        type: 'number',
        editable: false,
        sortable: false,
      },
      {
        field: 'net',
        headerName: 'Net',
        flex: 1,
        type: 'number',
        valueGetter: params => {
          const { gross = 0, tare = 0 } = params.row
          return gross - tare
        },
        sortable: false,
      },
    ]
    const filteredColumns = columns.filter(column => {
      if (!enablePullApartFeatures && column.field === 'itemCount') return false
      return true
    })
    return filteredColumns
  }, [enablePullApartFeatures])

  const bufferRef = useRef('')
  const timeoutRef = useRef(null)

  useEffect(() => {
    const handleKeyPress = e => {
      // Ignore key presses if they're from an input element
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return
      }

      // If Enter key is pressed, process the current buffer
      if (e.key === 'Enter') {
        const tag = bufferRef.current
        bufferRef.current = '' // Clear buffer

        if (tag) {
          setScannedTag(tag)
          setResult(`Scanning tag: ${tag}`)
          getFinishedGoodByTag({ tag })
        }
        return
      }

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Add character to buffer
      bufferRef.current += e.key

      // Set a backup timeout in case scanner doesn't send Enter
      timeoutRef.current = setTimeout(() => {
        const tag = bufferRef.current
        bufferRef.current = '' // Clear buffer

        if (tag) {
          setScannedTag(tag)
          setResult(`Scanning tag: ${tag}`)
          getFinishedGoodByTag({ tag })
        }
      }, 100)
    }

    window.addEventListener('keypress', handleKeyPress)

    return () => {
      window.removeEventListener('keypress', handleKeyPress)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [getFinishedGoodByTag])

  return (
    <DialogModal
      {...restProps}
      onClose={() => {
        resetQRCodeReader()
        // Clear scanner buffer on close
        bufferRef.current = ''
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        onClose()
      }}
      onSave={() => onAddFinishedGoods(selectedFinishedGoodIds)}
    >
      <Stack>
        <Grid px={3} pb={3} sx={{ height: '100%', overflow: 'scroll' }} container flexDirection="column" wrap="nowrap">
          <Grid item>
            {!isQrInitialized && (
              <Stack direction="row" spacing={2}>
                <Button
                  onClick={() => {
                    setIsQrLegacyMode(false)
                    setIsQrInitialized(true)
                  }}
                  variant="contained"
                >
                  Add via scanning a QR code
                </Button>

                <Button
                  onClick={() => {
                    setIsQrLegacyMode(true)
                    setIsQrInitialized(true)
                  }}
                  variant="contained"
                >
                  Add via uploading a QR code
                </Button>
              </Stack>
            )}

            {isQrInitialized && (
              <>
                <Stack direction="row" spacing={2} mb={2}>
                  {isQrLegacyMode && (
                    <Button
                      onClick={() => {
                        qrReaderRef?.current?.openImageDialog()
                      }}
                      variant="contained"
                    >
                      Upload
                    </Button>
                  )}

                  <Button
                    onClick={() => {
                      setResult(defaultQrCodeMessage)
                      setIsQrInitialized(false)
                    }}
                    variant="contained"
                  >
                    Done
                  </Button>
                </Stack>

                <Grid container flexDirection="column" justifyContent="center">
                  <QrReader
                    ref={qrReaderRef}
                    delay={300}
                    onError={err => handleError(err)}
                    onScan={data => handleScan(data)}
                    style={isQrLegacyMode ? { display: 'none' } : { width: '50%' }}
                    legacyMode={isQrLegacyMode}
                  />
                  <p>{result}</p>
                </Grid>
              </>
            )}
          </Grid>

          <Grid item mt={3}>
            <DataGridWithSearchV2
              density="compact"
              height="auto"
              rows={filteredFinishedGoods}
              columns={fgColumns}
              loading={isFetchingFinishedGoods || isFetchingScannedFinishedGood}
              apiRef={apiRef}
              editMode="row"
              checkboxSelection
              disableColumnMenu
              onSelectionModelChange={ids => {
                setSelectedFinishedGoodIds(ids)
                const newFinishedGoodsData = {}
                ids.forEach(id => {
                  if (!cachedFinishedGoodData[id]) {
                    const foundItem = filteredFinishedGoods.find(finishedGood => finishedGood.id === id)
                    if (foundItem) {
                      newFinishedGoodsData[id] = foundItem
                    }
                  }
                })
                if (Object.keys(newFinishedGoodsData).length > 0) {
                  setCachedFinishedGoodData(prevState => ({
                    ...prevState,
                    ...newFinishedGoodsData,
                  }))
                }
              }}
              selectionModel={selectedFinishedGoodIds}
              filter={queryOptions?.filter}
              setFilter={newFilter => setQueryOptions(prevState => ({ ...prevState, filter: newFilter }))}
              rowCount={finishedGoodsData?.totalResultsCount || 0}
              paginationMode="server"
              keepNonExistentRowsSelected
              useBackendSortAndFilter
              onPageChange={newPageNumber => setQueryOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
              onPageSizeChange={newPageSize => setQueryOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
              autoPageSize={false}
              pageSize={queryOptions.pageSize}
            />
          </Grid>
        </Grid>
        <Stack direction="row" px={3}>
          <Typography>{`${selectedFinishedGoodIds.length} Finished Goods`}</Typography>
          <Stack direction="row" flex="1" spacing={7} justifyContent="flex-end">
            <Typography>Totals:</Typography>
            <Typography>{`${totalGross} ${defaultYardUm}`}</Typography>
            <Typography>{`${totalTare} ${defaultYardUm}`}</Typography>
            <Typography>{`${totalNet} ${defaultYardUm}`}</Typography>
          </Stack>
        </Stack>
      </Stack>
    </DialogModal>
  )
}

FinishedGoodsModal.propTypes = {
  ...DialogModal.propTypes,
  apiRef: PropTypes.object,
}

FinishedGoodsModal.defaultProps = {
  ...DialogModal.defaultProps,
  title: 'Bulk Add Finished Goods',
  apiRef: null,
}

export default FinishedGoodsModal
